<ng-template #bottomSlotTmpl let-view="view">
  <div class="badges-container">
    @for (subview of view.views; track subview.id) {
      <rt-badge [text]="viewListCountersMap[subview.id]" color="counter-white-default" />
    }
  </div>
</ng-template>

<ng-template
  #leftSlotTmpl
  let-view="view"
  let-active="active"
  let-iconName="iconName"
  [ngTemplateType]="TItemLeftSlot"
>
  @if (!!iconName) {
    <rt-icon
      [name]="iconName"
      [colorToken]="active ? 'colorIconPrimaryDefault' : 'colorIconDefault'"
      size="small"
    />
  } @else {
    <rt-views-view-symbol
      [symbol]="view.symbol"
      [layoutType]="view.layout?.type"
      [iconColor]="active ? 'colorIconPrimaryDefault' : 'colorIconDefault'"
      [isPredefinedView]="!!view.codename"
    />
  }
</ng-template>

<ng-template #rightSlotTmpl let-count="count">
  @if (count !== null) {
    <rt-badge [text]="count" color="counter-white-default" />
  }
</ng-template>

<ng-template
  #sidebarItem
  let-view="view"
  let-draggable="draggable"
  let-iconName="iconName"
  let-isInGroup="isInGroup"
  [ngTemplateType]="TSidebarItem"
>
  <rt-sidebar-item
    #sidebarItemCmp
    [name]="view.name"
    [leftSlot]="leftSlotTmpl"
    [leftSlotContext]="{
      view: view,
      active: view.id === currentView()?.id,
      iconName: iconName,
    }"
    [rightSlot]="rightSlotTmpl"
    [rightSlotContext]="{ count: viewListCountersMap[view.id] }"
    [active]="view.id === currentView()?.id"
    [activeAnimation]="view.id === currentView()?.id && activeViewRunAnimation"
    [draggable]="draggable"
    [activeStateIndicatorEnabled]="true"
    type="heading"
    cdkDrag
    cdkDragBoundary=".drag-boundary"
    [cdkDragDisabled]="!draggable"
    (click)="onViewSelect(view)"
    (activeAnimationDone)="activeViewRunAnimation = false"
    [class.in-group]="isInGroup"
  />
</ng-template>

<ng-template #sidebarGroupItem let-view="view" [ngTemplateType]="TSidebarGroup">
  @let isActiveGroup = view.id === activeGroupViewId;
  @let isActiveItem = view.id === currentView()?.id;
  @let isActive = isActiveItem || isActiveGroup;
  @let isHovered = view.id === hoverItemId;
  @let isExpanded = expandedGroups[view.id];
  <div>
    <rt-sidebar-item
      [name]="view.name"
      [leftSlot]="leftSlotTmpl"
      [leftSlotContext]="{
        view,
        active: isActive,
        iconName: isHovered && !isExpanded ? 'arrow-drop-down' : null,
      }"
      [rightSlot]="rightSlotTmpl"
      [rightSlotContext]="{ count: viewListCountersMap[view.id] }"
      [bottomSlot]="bottomSlotTmpl"
      [bottomSlotContext]="{ view }"
      [textActive]="isActiveGroup"
      [activeAnimation]="isActiveItem && activeViewRunAnimation"
      [draggable]="false"
      [activeStateIndicatorEnabled]="true"
      type="heading"
      cdkDrag
      cdkDragBoundary=".drag-boundary"
      (mouseenter)="onItemMouseEnter(view)"
      (mouseleave)="onItemMouseLeave(view)"
      (click)="onViewSelect(view)"
      (activeAnimationDone)="activeViewRunAnimation = false"
      class="dynamic-height"
    />
    @if (isExpanded && !sidebarComponent.isMinimized()) {
      @for (child of view.views; track child.id) {
        <ng-template
          #elementInjector="elementInjector"
          elementInjector
          [ngTemplateOutlet]="sidebarItem"
          [ngTemplateOutletContext]="{
            view: child,
            first: false,
            draggable: false,
            isInGroup: true,
            iconName: 'vertical-line',
          }"
          [ngTemplateOutletInjector]="elementInjector.injector"
        />
      }
    }
  </div>
</ng-template>

<rt-sidebar
  #sidebarComponent
  [isExpanded]="isSidebarOpen"
  (expanded)="onSidebarExpand($event)"
  class="sidebar"
>
  @if (!sidebarComponent.isMinimized()) {
    <rt-sidebar-header [title]="dataSourceService.translations.dataSourceName | translate" />
  }

  @let views = views$ | async;
  <div>
    <div
      cdkDropList
      cdkDropListLockAxis="y"
      (cdkDropListDropped)="onReorder($event, views)"
      class="drag-boundary sidebar--items"
    >
      @for (
        view of views | slice: 0 : (showAll ? MAX_INT : showLimit);
        track view.id;
        let first = $first
      ) {
        <ng-template
          #elementInjector="elementInjector"
          elementInjector
          [ngTemplateOutlet]="view.type === 'view' ? sidebarItem : sidebarGroupItem"
          [ngTemplateOutletContext]="{ view, first, isInGroup: false, draggable: true }"
          [ngTemplateOutletInjector]="elementInjector.injector"
        />
      }
    </div>
    @if (views && views.length > showLimit) {
      <rt-sidebar-item
        [light]="true"
        [hoverable]="false"
        [name]="
          (showAll ? 'app.common.show_less' : 'app.views.view_page.show_more')
            | translate: { count: views.length - showLimit }
        "
        [icon]="showAll ? 'expand-less' : 'expand-more'"
        (click)="toggleShowAll()"
        type="heading"
      />
    }
  </div>
  <div class="sidebar--new">
    <rt-divider />
    <div
      [class.sidebar-minimized]="sidebarComponent.isMinimized()"
      layout
      margin-tb-small
      margin-lr-small-x
      [attr.padding-left-small-x]="!sidebarComponent.isMinimized() ? true : null"
    >
      <rt-add-new-button
        (click)="onCreateView()"
        [text]="'app.views.view_page.new_view' | translate"
        [alignHorizontal]="sidebarComponent.isMinimized() ? 'center' : 'left'"
        layout-full-width
      />
    </div>
    <div class="give-feedback-container">
      <a
        rt-button
        [href]="'app.views.view_page.feedback_link' | translate"
        [class.minimized]="sidebarComponent.isMinimized()"
        target="_blank"
        variant="plain"
        layout-full-width
      >
        @if (!sidebarComponent.isMinimized()) {
          <rt-icon name="open-in-new" margin-right-small-x />
          <span>{{ 'tlr.component_library_dialog.empty.give_feedback' | translate }}</span>
        } @else {
          <rt-icon name="open-in-new" />
        }
      </a>
    </div>
  </div>
</rt-sidebar>
